// 獨立 PTZ 攝影機的雲台（生效能力有 kinio_ptz；SPEC #contract-app-ui、F4 控制列）：能力模型命令經
// POST /v1/homes/{home}/things/{device}/commands（`kinio_ptz.jog` / `kinio_ptz.stop`），節奏見 jog.ts。
// 協定沒有變焦命令，這裡就不畫變焦 / 回原點 / 巡航（UX-11：按了一定失敗的不出現）。預置點走平台端點（DevicePresets，plan SPEC #multi-lens-ptz）。
// 多鏡頭（SPEC #multi-lens-ptz）：jog 帶焦點鏡頭 `lens`；焦點鏡頭 `pan`（只左右轉）時 ▲▼ 轉的是 pan_tilt 那顆、下方說明一行；`none` 方向盤停用。
// 通道（NVR / Station 底下的 ONVIF 攝影機）用 PtzPanel（VMS PTZ façade）。
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KinioError, type LensDecl } from '@kinio/web-sdk';
import { lensName } from '../../lib/lens';
import { DevicePresets } from './DevicePresets';
import type { VmsDevice } from '../../lib/devices';
import { deviceCommand } from '../../lib/deviceApi';
import { Jog, JogPump, jogRequest, type JogDir } from './jog';

type Dir = 'upleft' | 'up' | 'upright' | 'left' | 'right' | 'downleft' | 'down' | 'downright';
const PAD: (Dir | null)[] = ['upleft', 'up', 'upright', 'left', null, 'right', 'downleft', 'down', 'downright'];
const ARROW: Record<Dir, string> = { upleft: '↖', up: '↑', upright: '↗', left: '←', right: '→', downleft: '↙', down: '↓', downright: '↘' };
/** 畫面上的方向：pan 右 = 1、tilt 上 = 1（設備自己換算鏡像 / 倒裝）。 */
export const JOG_DIR: Record<Dir, JogDir> = {
  upleft: { pan: -1, tilt: 1 },
  up: { pan: 0, tilt: 1 },
  upright: { pan: 1, tilt: 1 },
  left: { pan: -1, tilt: 0 },
  right: { pan: 1, tilt: 0 },
  downleft: { pan: -1, tilt: -1 },
  down: { pan: 0, tilt: -1 },
  downright: { pan: 1, tilt: -1 },
};

export function DevicePtzPanel({
  cam,
  lens,
  decl,
  captureThumb,
  onUnsupported,
  onClose,
}: {
  cam: VmsDevice;
  /** 多鏡頭：畫面正在看的鏡頭（jog 帶它）；單鏡頭 = undefined。 */
  lens?: number;
  decl?: LensDecl | null;
  captureThumb?: () => Promise<Blob | null>;
  onUnsupported(): void;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const lensRef = useRef(lens);
  lensRef.current = lens;
  const focus = lens !== undefined ? decl?.lenses.find((l) => l.id === lens) : undefined;
  const ptzOf = focus?.ptz;
  const panTilt = decl?.panTiltLens ?? null;
  // 兩顆能力不同才出說明行（「左右：兩顆一起轉，上下：只有上鏡頭」）
  const note = decl && panTilt !== null && decl.lenses.some((l) => l.ptz === 'pan') ? t('ptz.lens_note', { name: lensName(t, decl, panTilt) }) : null;
  const [holding, setHolding] = useState<Dir | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const jog = useRef(new Jog());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const target = useRef(cam);
  target.current = cam;
  const unsupported = useRef(onUnsupported);
  unsupported.current = onUnsupported;

  const pump = useRef<JogPump | null>(null);
  if (!pump.current) {
    pump.current = new JogPump(
      (c) => {
        const d = target.current;
        const r = jogRequest(c, lensRef.current);
        const p = deviceCommand(d.apiEndpoint, d.homeId, d.aid, r.id, r.payload, r.waitSeconds);
        if (c.kind === 'stop') return p;
        // jog 最多等 1 秒 ack；逾時（504 COMMAND_TIMEOUT）= 命令已送到設備、只是沒等到 ack，不算錯（SPEC #ptz-jog-cadence）
        return p.catch((e: unknown) => {
          if (!(e instanceof KinioError && e.code === 'COMMAND_TIMEOUT')) throw e;
        });
      },
      (e) => {
        // 422 = 這台不支援（缺能力 / 能力上限擋下）：收起控制項（UX-11）
        if (e instanceof KinioError && e.status === 422) unsupported.current();
        else setErr(e instanceof KinioError ? t(`dctl.err_${e.code}`, { defaultValue: e.message || e.code }) : String(e));
      },
    );
  }

  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  /** 按著就睡到 nextResendAt 再 tick。 */
  const arm = useCallback(() => {
    clearTimer();
    const due = jog.current.nextResendAt;
    if (due === null) return;
    timer.current = setTimeout(
      () => {
        timer.current = null;
        for (const c of jog.current.tick(Date.now())) pump.current!.submit(c);
        arm();
      },
      Math.max(0, due - Date.now()),
    );
  }, []);

  const press = (d: Dir) => {
    if (!jog.current.pressed) {
      setErr(null);
      pump.current!.resetErrors();
    }
    for (const c of jog.current.press(JOG_DIR[d], Date.now())) pump.current!.submit(c);
    setHolding(d);
    arm();
  };

  const release = useCallback(() => {
    clearTimer();
    for (const c of jog.current.release()) pump.current!.submit(c);
    setHolding(null);
  }, []);

  const cancel = useCallback(() => {
    clearTimer();
    for (const c of jog.current.cancel(Date.now())) pump.current!.submit(c);
    setHolding(null);
  }, []);

  // 失焦 / 分頁到背景 / 關掉控制盤 → cancel（按著或可能還在轉就送 stop）
  useEffect(() => {
    const onBlur = () => cancel();
    const onVis = () => document.hidden && cancel();
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVis);
      cancel();
    };
  }, [cancel]);

  const holdProps = (d: Dir) => ({
    onPointerDown: (e: React.PointerEvent) => {
      e.preventDefault();
      (e.target as Element).setPointerCapture?.(e.pointerId);
      press(d);
    },
    onPointerUp: release,
    onPointerCancel: release,
    onLostPointerCapture: release,
    onKeyDown: (e: React.KeyboardEvent) => {
      if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) press(d);
    },
    onKeyUp: (e: React.KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') release();
    },
    'aria-pressed': holding === d,
  });

  return (
    <div className="ptz-panel" role="group" aria-label={t('ptz.title')} data-ptz="device" onClick={(e) => e.stopPropagation()}>
      <div className="ptz-head">
        <strong>{t('ptz.title')}</strong>
        <span className="spacer" />
        <button className="icon-btn" aria-label={t('common.close')} onClick={onClose}>
          ✕
        </button>
      </div>
      {err && <p className="error small">{err}</p>}
      <div className="ptz-body">
        <div className="ptz-pad" aria-disabled={ptzOf === 'none' || undefined} aria-description={ptzOf === 'none' ? t('ptz.lens_none') : (note ?? undefined)}>
          {PAD.map((d, i) =>
            d ? (
              <button key={d} className="ptz-btn" title={t(`ptz.dir_${d}`)} aria-label={t(`ptz.dir_${d}`)} data-dir={d} disabled={ptzOf === 'none'} {...holdProps(d)}>
                {ARROW[d]}
              </button>
            ) : (
              <span key={`c${i}`} aria-hidden="true" />
            ),
          )}
        </div>
      </div>
      {note && <p className="small muted ptz-lens-note">{note}</p>}
      <DevicePresets cam={cam} captureThumb={captureThumb} onBeforeMove={cancel} />
    </div>
  );
}
